// A tiny, framework-free ring buffer of the user's recent clicks, submits,
// and navigations — the sibling of `apiTrace.ts`. Same motivation: the
// in-app feedback report (src/components/feedback/) knows *what* the API
// said, but not *how* the user got there or what they pressed just before
// it happened. This module gives the report that breadcrumb trail,
// independent of any single component's own state.
//
// PRIVACY is the hard constraint here, exactly as in `apiTrace.ts`: this
// buffer is attached verbatim to feedback reports, which humans read. It
// must never record:
//   - the `value` of an `<input>` or `<textarea>` — could be anything the
//     user typed, including a password;
//   - keystrokes — no `keydown`/`keyup` listener is ever installed;
//   - text typed inside a rich-text editor (BlockNote uses
//     `[contenteditable]`) — a click inside one is still recorded (so the
//     report can say "the user clicked something in the editor toolbar"),
//     but its `label` is always omitted;
//   - anything from the feedback UI itself — the report must not describe
//     itself. A click entirely inside `[data-testid="feedback-dialog"]`, or
//     on an element whose own `data-testid` starts with `feedback-` (e.g.
//     the trigger button, which lives outside the dialog), is dropped.
//
// A navigation keeps its query string through `sanitizeQuery`: keys always,
// values only for ids, codes, enums and paging; free text is `[redacted]`.

import { sanitizeQuery } from './traceQuery'

/** The exact set of elements considered "interactive" for click recording —
 * mirrors the affordances `front/CLAUDE.md` asks every clickable element to
 * carry (`data-testid`) plus the native/ARIA widgets that may not. */
const INTERACTIVE_SELECTOR =
  '[data-testid], button, a[href], [role=button], [role=menuitem], [role=option], [role=tab], input[type=checkbox], input[type=radio], select, summary, label'

const FEEDBACK_DIALOG_SELECTOR = '[data-testid="feedback-dialog"]'
const MAX_LABEL_LEN = 40
const MAX_CLICKS = 25
const MAX_NAV = 10

export interface UiClickEntry {
  /** ISO 8601 timestamp of when the click was observed. */
  t: string
  kind: 'click'
  testid?: string
  tag: string
  role?: string
  /** `aria-label`, else `title`, else trimmed text content capped at 40
   * chars. Always omitted for a click inside `[contenteditable]`. */
  label?: string
}

export interface UiSubmitEntry {
  t: string
  kind: 'submit'
  testid?: string
  tag: 'form'
}

export interface UiNavEntry {
  t: string
  kind: 'nav'
  /** The matched route id, e.g. `/$org/documents/$docUid`. */
  route: string
  /** `location.pathname` only. */
  path: string
  /** The search string, sanitized by `sanitizeQuery` (keys kept, free-text values redacted). Absent when there is none. */
  query?: string
}

export type UiTraceEntry = UiClickEntry | UiSubmitEntry | UiNavEntry

let clicksAndSubmits: Array<UiClickEntry | UiSubmitEntry> = []
let navigations: UiNavEntry[] = []

function pushCapped<T>(buffer: T[], entry: T, max: number): void {
  buffer.push(entry)
  while (buffer.length > max) buffer.shift()
}

function truncateLabel(value: string): string {
  return value.length > MAX_LABEL_LEN ? value.slice(0, MAX_LABEL_LEN) : value
}

/** `aria-label` → `title` → trimmed text content, capped at 40 chars.
 * Returns `undefined` when none of the three yield anything. */
function resolveLabel(el: Element): string | undefined {
  const ariaLabel = el.getAttribute('aria-label')?.trim()
  if (ariaLabel) return truncateLabel(ariaLabel)

  const title = el.getAttribute('title')?.trim()
  if (title) return truncateLabel(title)

  const text = el.textContent?.trim()
  if (text) return truncateLabel(text)

  return undefined
}

function isInsideFeedbackUI(target: Element, el: Element | null): boolean {
  if (target.closest(FEEDBACK_DIALOG_SELECTOR)) return true
  const testid = el?.getAttribute('data-testid')
  return !!testid && testid.startsWith('feedback-')
}

/** Records one click, from `event.target`, walking up to the nearest
 * interactive ancestor. Never throws. Records nothing when: no interactive
 * ancestor is found, the click is inside the feedback dialog, or the
 * matched element's own testid starts with `feedback-`. */
export function recordClick(event: Event): void {
  try {
    const target = event.target
    if (!(target instanceof Element)) return

    const el = target.closest(INTERACTIVE_SELECTOR)
    if (isInsideFeedbackUI(target, el)) return
    if (!el) return

    const testid = el.getAttribute('data-testid') ?? undefined
    const role = el.getAttribute('role') ?? undefined
    const entry: UiClickEntry = {
      t: new Date().toISOString(),
      kind: 'click',
      tag: el.tagName.toLowerCase(),
    }
    if (testid) entry.testid = testid
    if (role) entry.role = role

    // Never surface text out of a rich-text editor — record the click
    // happened, not what it was near.
    if (!el.closest('[contenteditable]')) {
      const label = resolveLabel(el)
      if (label) entry.label = label
    }

    pushCapped(clicksAndSubmits, entry, MAX_CLICKS)
  } catch {
    // A trace must never break the interaction it is observing.
  }
}

/** Records one form submit. Never throws. Same feedback-UI exclusion as
 * `recordClick`. */
export function recordSubmit(event: Event): void {
  try {
    const target = event.target
    if (!(target instanceof Element)) return

    const form = target.closest('form')
    if (isInsideFeedbackUI(target, form)) return
    if (!form) return

    const testid = form.getAttribute('data-testid') ?? undefined
    const entry: UiSubmitEntry = { t: new Date().toISOString(), kind: 'submit', tag: 'form' }
    if (testid) entry.testid = testid

    pushCapped(clicksAndSubmits, entry, MAX_CLICKS)
  } catch {
    // Never break the submit it is observing.
  }
}

/** Records one route resolution. Never throws. */
export function recordNavigation(routeId: string, pathname: string, search?: string): void {
  try {
    const entry: UiNavEntry = { t: new Date().toISOString(), kind: 'nav', route: routeId, path: pathname }
    const query = search ? sanitizeQuery(search) : undefined
    if (query) entry.query = query
    pushCapped(navigations, entry, MAX_NAV)
  } catch {
    // Never break navigation over a tracing failure.
  }
}

/** The slice of the router instance this module needs — narrowed so it can
 * be exercised with a plain object in tests, with no `@tanstack/react-router`
 * dependency here. */
export interface NavigationTrackableRouter {
  subscribe: (event: 'onResolved', callback: (event: { toLocation: { pathname: string; searchStr?: string } }) => void) => () => void
  state: { matches: Array<{ routeId: string }> }
}

/**
 * Wires a TanStack Router instance into the nav ring buffer. Call this once
 * from `main.tsx`, right after `createRouter(...)`.
 *
 * There is deliberately no synthetic "record the current location" call
 * alongside the subscription: the router's own initial page load already
 * goes through its normal transition pipeline (its `Transitioner` calls
 * `router.load({ sync: true })` on mount, and `onResolved` fires for that
 * transition exactly like any other), so a page load already yields one
 * `onResolved` event with `router.state.matches` fully populated. Recording
 * an extra entry up front — before matches exist — only produced a
 * duplicate, worse-resolved (`route` = raw pathname instead of the route-id
 * template) first row. See spec 2026-09-06-17's follow-up fix.
 */
export function trackRouterNavigation(router: NavigationTrackableRouter): () => void {
  return router.subscribe('onResolved', (event) => {
    recordNavigation(router.state.matches.at(-1)?.routeId ?? event.toLocation.pathname, event.toLocation.pathname, event.toLocation.searchStr)
  })
}

/** Merges clicks/submits + navigations, sorted by time. Never throws. */
export function getUiTrace(): UiTraceEntry[] {
  try {
    return [...clicksAndSubmits, ...navigations].sort((a, b) => a.t.localeCompare(b.t))
  } catch {
    return []
  }
}

/** Test-only: clears both buffers so tests don't leak state into each other. */
export function resetUiTraceForTests(): void {
  clicksAndSubmits = []
  navigations = []
}
